import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Table,
  Input,
  Card,
  CardBody
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import UiContent from "../../Components/Common/UiContent";

const FILTER_OPTIONS = {
  "Day of Week": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
  "Table Occupancy": ["0-10%", "10-20%", "20-30%", "30-40%", "40-50%", "50-60%", "60-70%", "70-80%", "80-90%", "90-100%"],
  "Seat Occupancy": ["0-10%", "10-20%", "20-30%", "30-40%", "40-50%", "50-60%", "60-70%", "70-80%", "80-90%", "90-100%"],
  "Master Class": ["Alc Beverages", "Food", "Others"],
  "Universal Class": ["Alc Beverages", "Food", "Others"],
  "Courses": ["Alc Beverages", "Food", "Others", "Main"],
  "Food Items": ["Ceasr salad", "Pad thai", "Green Curry"],
  "Drinks Items": ["Espress", "Cappuccino", "Fresh OJ"],
  "Alcoholic / Non-Alcoholic": ["Alcoholic", "Non-Alcoholic"],
  "Hours of day": ["00:00", "01:00", "02:00", "03:00", "04:00"],
  "Discounts": ["50% Staff Discount", "AMEX 35%", "UOB 20%"],
  "Type": ["Bar Snacks", "Beer", "Buffet"],
  "Server": ["Sarah Johnsen", "Mike Chen", "Emily Devis"],
  "Portfolio Analysis": ["Star", "Cash Cow", "Puzzle", "Dog"],
  "Menu Item": ["Ceaser Salad", "Grilled Salmon", "Pad Thai"]
};

const DEFAULT_COLUMNS = [
  { key: "covers", label: "Covers", visible: true },
  { key: "avg", label: "Avg Check", visible: true },
  { key: "revenue", label: "Revenue", visible: true },
  { key: "itemcost", label: "Item Cost", visible: true },
  { key: "grossprofit", label: "Gross Profit", visible: true },
  { key: "totalcost", label: "Total Cost", visible: false },
  { key: "totalsales", label: "Total Sales", visible: false },
  { key: "salesmix", label: "Sales Mix", visible: false },
  { key: "revenuemix", label: "Revenue Mix", visible: false },
  { key: "contributionmargin", label: "Contribution Margin", visible: false },
  { key: "profitcontribution", label: "Profit Contribution", visible: false },
  { key: "popcontribution", label: "Popularity Contribution", visible: false },
  { key: "performancegroup", label: "Performance Group", visible: false },
];
const COLUMN_META = {
  covers: { type: "triple", bg: "#E3F2FD" },
  avg: { type: "triple", bg: "#E8F5E9" },
  revenue: { type: "triple", bg: "#FFF3E0" },
  itemcost: { type: "triple", bg: "#F3E5F5" },
  grossprofit: { type: "triple", bg: "#E0F7FA" },

  totalcost: { type: "double", bg: "#FCE4EC" },
  totalsales: { type: "double", bg: "#F1F8E9" },
  salesmix: { type: "double", bg: "#FFF8E1" },
  revenuemix: { type: "double", bg: "#EDE7F6" },
   contributionmargin: { type: "double", bg: "#F9FBE7", multiline: true },
  profitcontribution: { type: "double", bg: "#E8EAF6", multiline: true },
  popcontribution: { type: "double", bg: "#FBE9E7", multiline: true },
  performancegroup: { type: "double", bg: "#ECEFF1", multiline: true },
};

const MOCK_DATA = [
  {
    dateRange: "Feb 1 - Feb 28, 2026",
    day: "Monday",
    occupancy: "10-20%",
    coversCurrent: 41,
    coversLast: 38,
    avgCurrent: 227,
    avgLast: 33,
    revenueCurrent: 9319,
    revenueLast: 1241,
  },
  {
    dateRange: "Feb 1 - Feb 28, 2026",
    day: "Tuesday",
    occupancy: "0-10%",
    coversCurrent: 37,
    coversLast: 46,
    avgCurrent: 217,
    avgLast: 24,
    revenueCurrent: 8013,
    revenueLast: 1086,
  },
];

export default function PlaygroundNew() {
  const [filters, setFilters] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  // Add filter
  const handleAddFilter = (name) => {
    if (!filters.find(f => f.name === name)) {
      setFilters([...filters, { name, selected: [], open: false }]);
    }
  };

  // Toggle filter dropdown
  const toggleFilterDropdown = (name) => {
    setFilters(filters.map(f =>
      f.name === name ? { ...f, open: !f.open } : f
    ));
  };

  // Select option (UI only)
  const handleOptionSelect = (filterName, option) => {
    setFilters(filters.map(f => {
      if (f.name === filterName) {
        const exists = f.selected.includes(option);
        return {
          ...f,
          selected: exists
            ? f.selected.filter(o => o !== option)
            : [...f.selected, option],
        };
      }
      return f;
    }));
  };

  // Remove filter
  const removeFilter = (name) => {
    setFilters(filters.filter(f => f.name !== name));
  };

  // Apply Filters Button
  const handleApplyFilters = () => {
    setAppliedFilters([...filters]);
  };

  // Clear Filters
  const handleClearFilters = () => {
    setFilters([]);
    setAppliedFilters([]);
  };

  // Filtering Logic (ONLY uses appliedFilters)
  const filteredData = useMemo(() => {
    if (appliedFilters.length === 0) return [];

    let data = [...MOCK_DATA];

    appliedFilters.forEach(f => {
      if (f.name === "Day of Week" && f.selected.length > 0) {
        data = data.filter(row => f.selected.includes(row.day));
      }
    });

    return data;
  }, [appliedFilters]);

  const toggleColumn = (key) => {
    setColumns(columns.map(col =>
        col.key === key ? { ...col, visible: !col.visible } : col
    ));
    };

  return (
    <React.Fragment>
          <UiContent />
          <div className="page-content">              
            <Container fluid>
            <BreadCrumb title="Playground" pageTitle="Dashboard" />

            {/* Filters Section */}
            <Card className="mb-4">
                <CardBody>
                <Row className="align-items-end mb-3">
                    <Col md="3">
                        <label className="font-weight-500">Date Range</label>
                        <Input type="select">
                        <option>This Month</option>
                        <option>Last Month</option>
                        </Input>
                    </Col>

                    <Col md="9">
                        <div className="d-flex align-items-end flex-wrap gap-2">
                        <Dropdown
                            isOpen={addFilterOpen}
                            toggle={() => setAddFilterOpen(!addFilterOpen)}
                        >
                            <DropdownToggle caret color="primary">
                            + Add Filter
                            </DropdownToggle>
                            <DropdownMenu>
                            {Object.keys(FILTER_OPTIONS).map((name, index) => (
                                <DropdownItem key={index} onClick={() => handleAddFilter(name)}>
                                {name}
                                </DropdownItem>
                            ))}
                            </DropdownMenu>
                        </Dropdown>

                        
                        </div>
                    </Col>
                    </Row>

                {/* Dynamic Filters */}
                <Row className="g-3">
                    {filters.map((filter, index) => (
                        <Col md="3" key={index}>
                        <div className="filter-box p-2">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                            <label className="mb-0 font-weight-500">
                                {filter.name}
                            </label>
                            <Button
                                close
                                onClick={() => removeFilter(filter.name)}
                            />
                            </div>

                            <Dropdown
                            isOpen={filter.open}
                            toggle={() => toggleFilterDropdown(filter.name)}
                            >
                            <DropdownToggle
                                caret
                                className="w-100 text-left bg-light text-dark"
                                style={{borderColor: "lightgray"}}
                            >
                                {filter.selected.length > 0
                                ? `${filter.selected.length} selected`
                                : "Select"}
                            </DropdownToggle>

                            <DropdownMenu className="w-100 p-2 custom-dropdown-menu">
                            {FILTER_OPTIONS[filter.name].map((option, i) => {
                                const selected = filter.selected.includes(option);
                                return (
                                <div
                                    key={i}
                                    className={`custom-option ${selected ? "selected" : ""}`}
                                    onClick={() => handleOptionSelect(filter.name, option)}
                                >
                                    <input
                                    type="checkbox"
                                    checked={selected}
                                    readOnly
                                    />
                                    {/* <div className={`custom-checkbox ${selected ? "checked" : ""}`} /> */}
                                    <span>{option}</span>
                                </div>
                                );
                            })}
                            </DropdownMenu>
                            </Dropdown>

                            <div className="mt-2 d-flex flex-wrap gap-1">
                            {filter.selected.map((item, i) => (
                                <Badge
                                key={i}
                                color="danger"
                                
                                className="px-2 py-1"
                                style={{ cursor: "pointer", fontSize:"smaller" }}
                                onClick={() =>
                                    handleOptionSelect(filter.name, item)
                                }
                                >
                                {item} ×
                                </Badge>
                            ))}
                            </div>
                        </div>
                        </Col>
                    ))}
                    <Col md="3" key="aplbtn" className="pt-3">
                        <Button color="success" onClick={handleApplyFilters} className="me-2">
                            Apply Filters
                        </Button>

                        <Button color="danger" onClick={handleClearFilters}>
                            Clear All Filters
                        </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Results Section */}
            <Card>
                <CardBody>
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h5>Results ({filteredData.length} records)</h5>
                    </Col>

                    <Col className="text-end">
                        <Dropdown
                        isOpen={columnsOpen}
                        toggle={() => setColumnsOpen(!columnsOpen)}
                        >
                        <DropdownToggle caret color="light" className="border">
                            <i className="ri-filter-3-line" style={{ fontSize: 16 }}></i> Columns
                        </DropdownToggle>

                        <DropdownMenu right className="p-2">
                            {columns.map((col, index) => (
                            <div
                                key={index}
                                className="d-flex align-items-center mb-2"
                                style={{ cursor: "pointer" }}
                                onClick={() => toggleColumn(col.key)}
                            >
                                <Input
                                type="checkbox"
                                checked={col.visible}
                                readOnly
                                className="me-2"
                                />
                                {col.label}
                            </div>
                            ))}
                        </DropdownMenu>
                        </Dropdown>
                    </Col>
                    </Row>

                {filteredData.length === 0 ? (
                    <div className="text-center text-muted py-4">
                    Select filters and click "Apply Filters"
                    </div>
                ) : (
                    <div className="lovable-table-wrapper">
                        <Table responsive className="lovable-table">
                            <colgroup>
                            {/* Date Range fixed width */}
                            <col style={{ width: "200px" }} />

                            {/* Dynamic filter columns */}
                            {appliedFilters.map((_, i) => (
                                <col key={`filter-${i}`} style={{ width: "150px" }} />
                            ))}

                            {/* Metric sub columns (equal width) */}
                            {columns
                                .filter(col => col.visible)
                                .map(col => {
                                const meta = COLUMN_META[col.key];
                                const count = meta.type === "triple" ? 3 : 2;

                                return Array.from({ length: count }).map((_, i) => (
                                    <col key={`${col.key}-${i}`} style={{ width: "80px" }} />
                                ));
                                })}
                            </colgroup>
                            <thead>
                            <tr>
                            {/* Always First */}
                            <th rowSpan="2">Date Range</th>

                            {/* Dynamic Applied Filter Columns */}
                            {appliedFilters.map((filter, index) => (
                                <th key={index} rowSpan="2">
                                {filter.name}
                                </th>
                            ))}

                            {/* Metric Groups */}
                            {columns
                            .filter(col => col.visible)
                            .map(col => {
                                const meta = COLUMN_META[col.key];
                                const colSpan = meta.type === "triple" ? 3 : 2;

                                return (
                                <th
                                    key={col.key}
                                    colSpan={colSpan}
                                    className="group-header"
                                    style={{ backgroundColor: meta.bg }}
                                >
                                    {meta.multiline ? (
                                    col.label.split(" ").map((word, index) => (
                                        <div key={index}>{word}</div>
                                    ))
                                    ) : (
                                    col.label
                                    )}
                                </th>
                                );
                            })}
                            
                            </tr>

                            <tr className="sub-header">
                            {columns
                                .filter(col => col.visible)
                                .map(col => {
                                const meta = COLUMN_META[col.key];

                                if (meta.type === "triple") {
                                    return (
                                    <React.Fragment key={col.key}>
                                        <th>Current <br/>Year</th>
                                        <th>Last <br/>Year</th>
                                        <th>% <br/>Change</th>
                                    </React.Fragment>
                                    );
                                }

                                return (
                                    <React.Fragment key={col.key}>
                                    <th>Current <br/>Year</th>
                                    <th>% <br/>Change</th>
                                    </React.Fragment>
                                );
                                })}
                            </tr>
                            </thead>

                            <tbody>
                            {filteredData.map((row, index) => {
                                const coversChange = ((row.coversCurrent - row.coversLast) / row.coversLast) * 100;
                                const avgChange = ((row.avgCurrent - row.avgLast) / row.avgLast) * 100;
                                const revenueChange = ((row.revenueCurrent - row.revenueLast) / row.revenueLast) * 100;

                                return (
                                <tr key={index}>
                                {/* Date Range */}
                                <td>{row.dateRange}</td>

                                {/* Dynamic Applied Filter Columns */}
                                {appliedFilters.map((filter, i) => {
                                    if (filter.name === "Day of Week") {
                                    return <td key={i}>{row.day}</td>;
                                    }

                                    if (filter.name === "Table Occupancy") {
                                    return <td key={i}>{row.occupancy}</td>;
                                    }

                                    return <td key={i}>-</td>;
                                })}

                                {/* Metrics */}
                                {columns
                                .filter(col => col.visible)
                                .map(col => {
                                    const meta = COLUMN_META[col.key];

                                    // mock dynamic values (replace with real API later)
                                    const current = Math.floor(Math.random() * 1000) + 100;
                                    const last = Math.floor(Math.random() * 1000) + 100;
                                    const change = ((current - last) / last) * 100;

                                    if (meta.type === "triple") {
                                    return (
                                        <React.Fragment key={col.key}>
                                        <td>{current}</td>
                                        <td>{last}</td>
                                        <td className={change >= 0 ? "positive" : "negative"}>
                                            {change.toFixed(0)}%
                                        </td>
                                        </React.Fragment>
                                    );
                                    }

                                    // DOUBLE COLUMN TYPE
                                    return (
                                    <React.Fragment key={col.key}>
                                        <td>{current}</td>
                                        <td className={change >= 0 ? "positive" : "negative"}>
                                        {change.toFixed(0)}%
                                        </td>
                                    </React.Fragment>
                                    );
                                })}
                                </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                        </div>
                )}
                </CardBody>
            </Card>
            </Container>
        </div>
    </React.Fragment>
  );
}